import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { HeroBanner } from '../components/HeroBanner';
import { IconChoiceCard } from '../components/IconChoiceCard';
import { KioskButton } from '../components/KioskButton';
import { KioskFrame } from '../components/KioskFrame';
import { Mascot } from '../components/Mascot';
import { useIsPrinterPaperOut } from '../components/PrinterBanner';
import { useKiosk, type DeliveryMethod, type InvoiceKind } from '../lib/session';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CARRIER_RE = /^\/[0-9A-Z.+-]{7}$/;
const TAX_ID_RE = /^\d{8}$/;

/** 選項圖示，裁自業主 2026-08-19 交付素材（6_05_*／6_06_*）。 */
const ICONS = {
  'paper-qr': '/brand/opt-paper-qr.png',
  email: '/brand/opt-email.png',
  line: '/brand/opt-line.png',
  carrier: '/brand/opt-carrier.png',
  paper: '/brand/opt-paper-invoice.png',
  donate: '/brand/opt-donate.png',
  company: '/brand/opt-company.png',
} as const;

const DELIVERY_OPTIONS: { method: DeliveryMethod; titleKey: string; descKey: string }[] = [
  { method: 'paper-qr', titleKey: 'delivery.paperQr', descKey: 'delivery.paperQrDesc' },
  { method: 'email', titleKey: 'delivery.email', descKey: 'delivery.emailDesc' },
  { method: 'line', titleKey: 'delivery.line', descKey: 'delivery.lineDesc' },
];

const INVOICE_OPTIONS: { kind: InvoiceKind; titleKey: string; descKey: string }[] = [
  { kind: 'carrier', titleKey: 'invoice.carrier', descKey: 'invoice.carrierDesc' },
  { kind: 'paper', titleKey: 'invoice.paper', descKey: 'invoice.paperDesc' },
  { kind: 'donate', titleKey: 'invoice.donate', descKey: 'invoice.donateDesc' },
  { kind: 'company', titleKey: 'invoice.company', descKey: 'invoice.companyDesc' },
];

/**
 * 取票方式與發票開立 — 業主 2026-08-19 設計稿第 6 頁。
 *
 * ## 兩頁併成一頁
 *
 * 原本收票（Flow 3.8）與發票（Flow 3.9）各佔一頁，設計稿把它們畫在同一頁。兩者
 * 都只是「開始付款前先設定好」的事，各自的選項不多，1920 高放得下；分兩頁等於多
 * 一次換頁與一次「下一步」，而中間那一步顧客完全不需要停下來思考。
 *
 * 舊的 `/invoice` 路由已移除——留著會是一個進得去、卻沒有收票方式可讀的半截畫面。
 *
 * ## 兩段都要填完才能往下
 *
 * 併頁之後「下一步」要同時擋住兩段的必填，任一段沒選就不能付款。缺紙時紙本 QR
 * 會被停用並自動清掉先前的選擇（AC-C4-1）。
 */
export function DeliveryInvoiceScreen(): JSX.Element {
  const navigate = useNavigate();
  const { t, draft, patchDraft } = useKiosk();
  const paperOut = useIsPrinterPaperOut();

  // 缺紙時若先前已選紙本 QR → 自動清空（強制走 Email/LINE）。
  const initialMethod = paperOut && draft.delivery === 'paper-qr' ? null : draft.delivery;
  const [method, setMethod] = useState<DeliveryMethod | null>(initialMethod);
  const [email, setEmail] = useState(draft.buyerEmail);
  const [emailTouched, setEmailTouched] = useState(false);

  const [kind, setKind] = useState<InvoiceKind | null>(draft.invoice?.kind ?? null);
  const [carrier, setCarrier] = useState(
    draft.invoice?.kind === 'carrier' ? (draft.invoice.value ?? '') : '',
  );
  const [taxId, setTaxId] = useState(
    draft.invoice?.kind === 'company' ? (draft.invoice.value ?? '') : '',
  );

  if (draft.schedule === null) return <Navigate to="/home" replace />;

  const emailValid = EMAIL_RE.test(email.trim());
  const carrierValid = CARRIER_RE.test(carrier.trim().toUpperCase());
  const taxIdValid = TAX_ID_RE.test(taxId.trim());
  const canProceed =
    method !== null &&
    (method !== 'email' || emailValid) &&
    kind !== null &&
    (kind !== 'carrier' || carrierValid) &&
    (kind !== 'company' || taxIdValid);

  function proceed(): void {
    if (method === null || kind === null) return;
    const value =
      kind === 'carrier'
        ? carrier.trim().toUpperCase()
        : kind === 'company'
          ? taxId.trim()
          : undefined;
    patchDraft({
      delivery: method,
      buyerEmail: method === 'email' ? email.trim() : '',
      invoice: value !== undefined ? { kind, value } : { kind },
    });
    navigate('/payment');
  }

  return (
    <KioskFrame bleed>
      <HeroBanner height={340} align="left">
        <Mascot size={210} pose="smile" />
      </HeroBanner>

      <h1 className="k-h1" style={{ textAlign: 'center', marginTop: 8, marginBottom: 6 }}>
        {t('checkout.title')}
      </h1>
      <p className="k-subtitle k-subtitle--flanked">{t('checkout.lead')}</p>

      <h2 className="k-sectionhead k-column">
        {t('delivery.title')}
        <span className="k-sectionhead__note">ⓘ {t('delivery.note')}</span>
      </h2>

      <div className="k-column">
        {paperOut && (
          <p
            data-testid="kiosk-delivery-digital-only"
            data-error-code="HW-004"
            className="k-error"
            style={{ marginBottom: 16 }}
          >
            ⚠️ {t('delivery.paperOut')}
          </p>
        )}

        <div className="k-payopts k-payopts--3">
          {DELIVERY_OPTIONS.map((o) => (
            <IconChoiceCard
              key={o.method}
              icon={ICONS[o.method]}
              title={t(o.titleKey)}
              desc={t(o.descKey)}
              active={method === o.method}
              disabled={paperOut && o.method === 'paper-qr'}
              testId={`kiosk-delivery-${o.method}`}
              onClick={() => setMethod(o.method)}
            />
          ))}
        </div>

        {method === 'email' && (
          <div className="k-fieldrow">
            <label className="k-fieldrow__label" htmlFor="checkout-email">
              {t('delivery.emailPrompt')}
            </label>
            <input
              id="checkout-email"
              className="k-input"
              type="email"
              inputMode="email"
              value={email}
              placeholder="name@example.com"
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setEmailTouched(true)}
            />
          </div>
        )}
        {method === 'email' && emailTouched && !emailValid && (
          <p className="k-error">{t('delivery.emailInvalid')}</p>
        )}
      </div>

      <hr className="k-hairline" />

      <h2 className="k-sectionhead k-column">{t('invoice.title')}</h2>

      <div className="k-column">
        <div className="k-payopts k-payopts--2">
          {INVOICE_OPTIONS.map((o) => (
            <IconChoiceCard
              key={o.kind}
              icon={ICONS[o.kind]}
              title={t(o.titleKey)}
              desc={t(o.descKey)}
              active={kind === o.kind}
              testId={`kiosk-invoice-${o.kind}`}
              onClick={() => setKind(o.kind)}
            />
          ))}
        </div>

        {kind === 'carrier' && (
          <div className="k-fieldrow">
            <label className="k-fieldrow__label" htmlFor="checkout-carrier">
              {t('invoice.carrierPrompt')}
            </label>
            <input
              id="checkout-carrier"
              className="k-input"
              value={carrier}
              placeholder="/MOSA1234"
              onChange={(e) => setCarrier(e.target.value)}
            />
          </div>
        )}
        {kind === 'company' && (
          <div className="k-fieldrow">
            <label className="k-fieldrow__label" htmlFor="checkout-taxid">
              {t('invoice.taxIdPrompt')}
            </label>
            <input
              id="checkout-taxid"
              className="k-input"
              value={taxId}
              inputMode="numeric"
              placeholder="12345678"
              onChange={(e) => setTaxId(e.target.value)}
            />
          </div>
        )}

        <p className="k-text k-text--dim" style={{ marginTop: 14 }}>
          ⓘ {t('invoice.note')}
        </p>
      </div>

      <hr className="k-hairline" />

      <div className="k-footbar k-footbar--center">
        <KioskButton variant="secondary" onClick={() => navigate(-1)}>
          ← {t('common.back')}
        </KioskButton>
        <KioskButton variant="primary" disabled={!canProceed} onClick={proceed}>
          {t('checkout.toPayment')} →
        </KioskButton>
      </div>
    </KioskFrame>
  );
}
